import type { Meeting } from "@sales-ops-amplify/shared";
import { Link } from "react-router-dom";
import { useMeetingStatusAction } from "@/hooks/useMeetings";

function formatWhen(iso: string, timezone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
    });
  } catch {
    return d.toLocaleString();
  }
}

export function MeetingWorkbench({
  meeting,
  onClose,
  onUpdated,
}: {
  meeting: Meeting | null;
  onClose: () => void;
  onUpdated?: (meeting: Meeting) => void;
}) {
  const statusMut = useMeetingStatusAction();

  function runStatus(
    action: "complete" | "no_show" | "cancel",
  ) {
    if (!meeting) return;
    statusMut.mutate(
      { meetingId: meeting.meetingId, action },
      {
        onSuccess: (res) => {
          onUpdated?.(res.meeting);
        },
      },
    );
  }

  if (!meeting) {
    return (
      <aside className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
        Select a meeting to view details and outcomes.
      </aside>
    );
  }

  return (
    <aside className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {meeting.title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatWhen(meeting.startAt, meeting.timezone)} · {meeting.status}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Close
        </button>
      </div>

      {meeting.guests.length > 0 && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Guests
          </p>
          <ul className="mt-1 space-y-1 text-sm">
            {meeting.guests.map((g) => (
              <li key={g.email}>
                {g.name ? `${g.name} ` : ""}
                <span className="text-muted-foreground">&lt;{g.email}&gt;</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(meeting.leadName || meeting.leadId) && (
        <div className="text-sm">
          <span className="text-muted-foreground">Contact: </span>
          {meeting.leadId ? (
            <Link
              to={`/user/leads?q=${encodeURIComponent(meeting.leadName || meeting.leadId)}`}
              className="text-brand hover:underline"
            >
              {meeting.leadName || meeting.leadId}
            </Link>
          ) : (
            meeting.leadName
          )}
        </div>
      )}

      {meeting.accountId && (
        <div className="text-sm">
          <span className="text-muted-foreground">Company: </span>
          <Link
            to={`/user/accounts/${meeting.accountId}`}
            className="text-brand hover:underline"
          >
            {meeting.accountId}
          </Link>
        </div>
      )}

      {meeting.opportunityId && (
        <div className="text-sm">
          <span className="text-muted-foreground">Deal: </span>
          <Link
            to={`/deals/${meeting.opportunityId}`}
            className="text-brand hover:underline"
          >
            {meeting.opportunityId.slice(0, 8)}…
          </Link>
        </div>
      )}

      {meeting.conferencingUrl && (
        <a
          href={meeting.conferencingUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground hover:opacity-90"
        >
          Join meeting
        </a>
      )}

      {meeting.status === "scheduled" && (
        <div className="flex flex-wrap gap-2 border-t border-border pt-3">
          <button
            type="button"
            disabled={statusMut.isPending}
            onClick={() => runStatus("complete")}
            className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
          >
            Mark completed
          </button>
          <button
            type="button"
            disabled={statusMut.isPending}
            onClick={() => runStatus("no_show")}
            className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
          >
            No-show
          </button>
          <button
            type="button"
            disabled={statusMut.isPending}
            onClick={() => runStatus("cancel")}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs text-destructive hover:bg-destructive/10"
          >
            Cancel
          </button>
        </div>
      )}

      {meeting.outcomeNotes && (
        <p className="text-sm text-muted-foreground">{meeting.outcomeNotes}</p>
      )}
    </aside>
  );
}
